// Cross-window "reload now" signal for app updates.
//
// When POS applies a deploy update, the CFD window must reload too —
// otherwise it keeps running the old code while POS runs new, which
// causes wire-format drift on cartChannel/sessionChannel and silent CFD
// drift until its own polling cycle (up to ~2 min) catches up.
//
// Transport mirrors cartChannel/sessionChannel for consistency, but uses
// localStorage as the PRIMARY path: BroadcastChannel delivery races the
// page's reload-teardown (the message may not flush before the channel
// closes), whereas a localStorage write is synchronous and the resulting
// `storage` event is guaranteed to fire on sibling windows because the
// value lands before the calling tab unloads.
//
// Direction is one-way: POS broadcasts, CFD listens. CFD never broadcasts
// — we don't want a CFD-initiated reload (e.g. its own UpdateAutoApply)
// to bounce back into the POS window during staff use.

const STORAGE_KEY = '__waio_app_reload_signal__'
const CHANNEL_NAME = 'waio.app.reload'

/** POS calls this right before `window.location.reload()` to tell sibling
 *  windows (CFD) to reload too. Best-effort across both transports; if
 *  neither is available the CFD will catch up on its next /version.json
 *  poll (up to ~2 min later). */
export function broadcastReloadNow(): void {
  // localStorage path first — synchronous write, storage event is
  // guaranteed to deliver to other windows even if the calling tab is
  // about to unload.
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, String(Date.now()))
    }
  } catch {
    // Quota / private mode — silent fail, fall through to BC.
  }

  // BroadcastChannel as a faster path. Sometimes the BC microtask flushes
  // before reload tears down; when it doesn't, the storage event above
  // still delivers.
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const bc = new BroadcastChannel(CHANNEL_NAME)
      bc.postMessage({ type: 'reload', ts: Date.now() })
      bc.close()
    }
  } catch {
    // Older WebView2 throws on construct — covered by the storage path.
  }
}

/** CFD subscribes to this on mount. Returns an unsubscribe function. */
export function onReloadSignal(callback: () => void): () => void {
  let bc: BroadcastChannel | null = null
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      bc = new BroadcastChannel(CHANNEL_NAME)
      bc.onmessage = (ev) => {
        if (ev.data && typeof ev.data === 'object' && ev.data.type === 'reload') {
          callback()
        }
      }
    }
  } catch {
    bc = null
  }

  const onStorage = (ev: StorageEvent) => {
    if (ev.key === STORAGE_KEY && ev.newValue) {
      callback()
    }
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage)
  }

  return () => {
    if (bc !== null) {
      try { bc.close() } catch { /* ignore */ }
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage)
    }
  }
}
